import React, { useEffect, useState } from 'react';
import {
  Box, Text, Button, IconButton, Input, Switch, Skeleton, FormControl, FormLabel, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton
} from '@chakra-ui/react';
import { Add, Edit } from '@mui/icons-material';

import AdminLayout from '../components/admin/AdminLayout';
import { getAllGenres, addGenre, updateGenre, deleteGenre } from '../services/GenreServices';
import { getAllCategories, addCategory, updateCategory, deleteCategory } from '../services/CategoryServices';
import { getAllProducts } from '../services/ProductServices';

// Genres (store sections like Casual/Formal) hold categories (Polo Shirts, Jeans…)
const CategoriesforAdmin = () => {

  const toast = useToast();
  const [genres, setGenres] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productCounts, setProductCounts] = useState({});
  const [dialog, setDialog] = useState(null); // { kind: 'genre'|'category', item?, genreId? }

  const load = () => {
    getAllGenres().then(r => setGenres(r.allGenres || []));
    getAllCategories().then(r => setCategories(r.allCategories || []));
    getAllProducts().then(r => {
      const counts = {};
      (r.allProducts || []).forEach(p => { counts[p.category] = (counts[p.category] || 0) + 1; });
      setProductCounts(counts);
    });
  };
  useEffect(load, []);

  const save = async ({ name, status }) => {
    const { kind, item, genreId } = dialog;
    try {
      if (kind === 'genre') {
        item ? await updateGenre(item._id, name, status) : await addGenre(name, status);
      } else {
        item ? await updateCategory(item._id, name, item.genre, status) : await addCategory(name, genreId, status);
      }
      toast({ title: 'Saved', status: 'success', duration: 1500, position: 'top' });
      setDialog(null);
      load();
    } catch (e) {
      toast({ title: 'Could not save', description: 'Section names must be unique.', status: 'error', duration: 3000 });
    }
  };

  const remove = async () => {
    const { kind, item } = dialog;
    try {
      kind === 'genre' ? await deleteGenre(item._id) : await deleteCategory(item._id);
      toast({ title: 'Deleted', status: 'success', duration: 1500, position: 'top' });
      setDialog(null);
      load();
    } catch (e) {
      toast({ title: 'Could not delete', status: 'error', duration: 3000 });
    }
  };

  // Deleting is blocked while something still depends on the item
  const blockReason = (() => {
    if (!dialog?.item) return null;
    if (dialog.kind === 'genre') {
      const n = categories.filter(c => c.genre === dialog.item._id).length;
      return n ? `Move or delete its ${n} ${n === 1 ? 'category' : 'categories'} first.` : null;
    }
    const n = productCounts[dialog.item._id] || 0;
    return n ? `${n} ${n === 1 ? 'product uses' : 'products use'} this category. Hide it instead, or move the products first.` : null;
  })();

  return (
    <AdminLayout
      title='Categories'
      subtitle='Sections and categories shown in the store menu'
      actions={<Button variant='ink' height='36px' px='16px' fontSize='14px' leftIcon={<Add fontSize='small' />} onClick={() => setDialog({ kind: 'genre' })} >Section</Button>}
    >
      {genres === null
        ? Array(2).fill(null).map((_, i) => <Skeleton key={i} height='160px' borderRadius='12px' mb={3} />)
        : genres.map(genre => {
          const list = categories.filter(c => c.genre === genre._id);
          return (
            <Box key={genre._id} bg='raano.white' borderRadius='12px' border='1px solid' borderColor='raano.border' mb={3} overflow='hidden' >
              <Box display='flex' alignItems='center' justifyContent='space-between' px={4} py={3} bg='raano.surface' >
                <Box>
                  <Text fontWeight={800} >{genre.name}</Text>
                  <Text fontSize={12} color={genre.status ? 'blackAlpha.600' : 'orange.600'} >{genre.status ? 'Shown in menu' : 'Hidden from menu'} · {list.length} {list.length === 1 ? 'category' : 'categories'}</Text>
                </Box>
                <IconButton size='sm' variant='ghost' borderRadius='full' aria-label={`Edit ${genre.name}`} icon={<Edit fontSize='small' />} onClick={() => setDialog({ kind: 'genre', item: genre })} />
              </Box>
              {list.map(c => (
                <Box key={c._id} as='button' width='100%' textAlign='left' display='flex' alignItems='center' justifyContent='space-between' px={4} py={3} borderTop='1px solid' borderColor='raano.border' _hover={{ bg: '#FAFAFA' }} onClick={() => setDialog({ kind: 'category', item: c })} >
                  <Box>
                    <Text fontSize={15} fontWeight={600} opacity={c.status ? 1 : 0.6} >{c.name}</Text>
                    <Text fontSize={12} color={c.status ? 'blackAlpha.600' : 'orange.600'} >{productCounts[c._id] || 0} products{c.status ? '' : ' · Hidden'}</Text>
                  </Box>
                  <Edit sx={{ fontSize: 18, color: 'rgba(0,0,0,0.4)' }} />
                </Box>
              ))}
              <Button width='100%' variant='ghost' borderRadius={0} borderTop='1px solid' borderColor='raano.border' color='raano.black' fontWeight={500} fontSize='14px' _hover={{ bg: '#FAFAFA' }} leftIcon={<Add fontSize='small' />} onClick={() => setDialog({ kind: 'category', genreId: genre._id })} >
                Add category to {genre.name}
              </Button>
            </Box>
          );
        })}

      <EditDialog
        dialog={dialog}
        blockReason={blockReason}
        onClose={() => setDialog(null)}
        onSave={save}
        onDelete={remove}
      />
    </AdminLayout>
  )
}

const EditDialog = ({ dialog, blockReason, onClose, onSave, onDelete }) => {
  const [name, setName] = useState('');
  const [status, setStatus] = useState(true);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setName(dialog?.item?.name || '');
    setStatus(dialog?.item ? dialog.item.status !== false : true);
    setConfirming(false);
  }, [dialog]);

  const noun = dialog?.kind === 'genre' ? 'section' : 'category';

  return (
    <Modal isOpen={!!dialog} onClose={onClose} isCentered >
      <ModalOverlay />
      <ModalContent mx={4} borderRadius='12px' >
        <ModalHeader fontSize={18} >{dialog?.item ? `Edit ${noun}` : `New ${noun}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4} >
            <FormLabel fontSize={14} mb={1} >Name</FormLabel>
            <Input autoFocus placeholder={dialog?.kind === 'genre' ? 'e.g. Ethnic' : 'e.g. Kurtas'} value={name} onChange={(e) => setName(e.target.value)} />
          </FormControl>
          <FormControl display='flex' alignItems='center' justifyContent='space-between' >
            <FormLabel htmlFor='item-visible' mb={0} fontSize={14} >Show in store menu</FormLabel>
            <Switch id='item-visible' isChecked={status} onChange={(e) => setStatus(e.target.checked)} />
          </FormControl>
          {dialog?.item && (
            confirming
              ? <Box mt={5} p={3} bg='red.50' borderRadius='8px' >
                <Text fontSize={14} fontWeight={600} >Delete “{dialog.item.name}”? This can't be undone.</Text>
                <Box display='flex' gap={2} mt={2} >
                  <Button size='sm' borderRadius='full' onClick={() => setConfirming(false)} >Keep</Button>
                  <Button size='sm' borderRadius='full' colorScheme='red' onClick={onDelete} >Delete</Button>
                </Box>
              </Box>
              : <Box mt={5} >
                <Button size='sm' variant='ghost' colorScheme='red' isDisabled={!!blockReason} onClick={() => setConfirming(true)} >Delete {noun}</Button>
                {blockReason && <Text fontSize={12} color='blackAlpha.600' mt={1} >{blockReason}</Text>}
              </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2} >
          <Button variant='wipe' height='40px' px='18px' fontSize='14px' onClick={onClose} >Cancel</Button>
          <Button variant='ink' height='40px' px='18px' fontSize='14px' isDisabled={!name.trim()} onClick={() => onSave({ name: name.trim(), status })} >Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CategoriesforAdmin;
